import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { TimeOffInput } from './types';

export const timeOffKeys = {
  all: ['time-off'] as const,
  lists: () => [...timeOffKeys.all, 'list'] as const,
  list: (p: api.TimeOffListParams) => [...timeOffKeys.lists(), p] as const,
  detail: (id: string | number) => [...timeOffKeys.all, 'detail', id] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

export function useTimeOff(params: api.TimeOffListParams = {}) {
  return useQuery({
    queryKey: timeOffKeys.list(params),
    queryFn: () => api.listTimeOff(params),
    placeholderData: keepPreviousData,
  });
}

export function useTimeOffItem(id: string | number) {
  return useQuery({
    queryKey: timeOffKeys.detail(id),
    queryFn: () => api.getTimeOff(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreateTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TimeOffInput) => api.createTimeOff(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeOffKeys.lists() });
      toast.success('Request filed');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateTimeOff(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<TimeOffInput>) => api.updateTimeOff(id, input),
    onSuccess: (request) => {
      qc.invalidateQueries({ queryKey: timeOffKeys.lists() });
      qc.setQueryData(timeOffKeys.detail(id), request);
      toast.success('Request updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deleteTimeOff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeOffKeys.lists() });
      toast.success('Request deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useApproveTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.approveTimeOff(id),
    onSuccess: (request) => {
      qc.invalidateQueries({ queryKey: timeOffKeys.lists() });
      qc.setQueryData(timeOffKeys.detail(request.public_id), request);
      toast.success('Request approved');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRejectTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.rejectTimeOff(id),
    onSuccess: (request) => {
      qc.invalidateQueries({ queryKey: timeOffKeys.lists() });
      qc.setQueryData(timeOffKeys.detail(request.public_id), request);
      toast.success('Request rejected');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
